import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  Grid,
  Divider,
  Checkbox,
  Select,
  Tooltip,
} from '@chakra-ui/react'
import { createFixedExpensesSchema, updateFixedExpensesSchema, type CreateFixedExpensesFormData, type UpdateFixedExpensesFormData } from '@/lib/validations/auth'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'
import { Update } from 'vite/types/hmrPayload.js'

interface FixedExpensesFormProps {
  fixedExpensesId?: number
  onSuccess: () => void
  onCancel: () => void
}

export function FixedExpensesForm({ fixedExpensesId, onSuccess, onCancel }: FixedExpensesFormProps) {
  const colors = useThemeColors()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditMode = !!fixedExpensesId
  const { showError, showSuccess } = useToastNotification()

  type SelectedItem = {
    parent_id: number
    name: string
    }
  const [shippingLineOptions, setShippingLineOption] = useState<{ id: number; name: string }[]>([])
  const [cypaOptions, setCypaOption] = useState<{ id: number; name: string }[]>([])
  type ContainerSize = '20ft' | '40ft'
  const [containerSizeOptions, setContainerSizeOptions] = useState<ContainerSize[]>(['20ft', '40ft'])

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<CreateFixedExpensesFormData | UpdateFixedExpensesFormData>({
    resolver: zodResolver(isEditMode ? updateFixedExpensesSchema : createFixedExpensesSchema),
    defaultValues: {
        shipping_line_id: undefined,
        cypa_id_from: undefined,
        cypa_id_to: undefined,
        container_size: '20ft',
        docs_fee: 0,
        stack_run: 0,
        expenses: 0,
        online_booking_fee: null,
    },
  })

  useEffect(() => {
  const fetchData = async () => {
    try {
      // Always fetch Shipping Line Template Data Options
      const shippingLineDataResponse = await apiClient.get(`shipping-lines`);
      const shippingLineData = shippingLineDataResponse.data?.data || shippingLineDataResponse.data || [];
      setShippingLineOption(shippingLineData);

      // Fetch Transaction Line Template Data Options
      const cypaDataResponse = await apiClient.get(`container-yards/yard-list`, { params: { is_active: 1 } });
      const cypaData = cypaDataResponse.data?.data || cypaDataResponse.data || [];
      setCypaOption(cypaData);

      // If edit mode, fetch shipping line details
      if (isEditMode && fixedExpensesId) {
        const response = await apiClient.get(`/fixed-expenses/${fixedExpensesId}`);
        const fixedExpense = response.data?.data || response.data || {};

        reset({
            shipping_line_id: fixedExpense.shipping_line_id || undefined,
            cypa_id_from: fixedExpense.cypa_id_from || undefined,
            cypa_id_to: fixedExpense.cypa_id_to || undefined,
            container_size: fixedExpense.container_size || '20ft',
            docs_fee: fixedExpense.docs_fee || 0,
            stack_run: fixedExpense.stack_run || 0,
            expenses: fixedExpense.expenses || 0,
            online_booking_fee: fixedExpense.online_booking_fee ?? null,
        });
      }
    } catch (error: any) {
      showError(error);
    }
  };
  fetchData();
}, [isEditMode, fixedExpensesId, reset, showError]);
  

  const onSubmit = async (data: CreateFixedExpensesFormData | UpdateFixedExpensesFormData) => {
    setIsSubmitting(true)
    try {
      const payload = {
        shipping_line_id: data.shipping_line_id,
        cypa_id_from: data.cypa_id_from,
        cypa_id_to: data.cypa_id_to,
        container_size: data.container_size,
        docs_fee: data.docs_fee,
        stack_run: data.stack_run,
        expenses: data.expenses,
        online_booking_fee: data.online_booking_fee ?? 0,
        total_expenses: data.docs_fee + data.stack_run + data.expenses + (data.online_booking_fee ?? 0),
    }

      // 🔎 FRONTEND DUPLICATE CHECK
    const checkResponse = await apiClient.get('/fixed-expenses', {
      params: {
        shipping_line_id: data.shipping_line_id,
        cypa_id_from: data.cypa_id_from,
        cypa_id_to: data.cypa_id_to,
        container_size: data.container_size,
      },
    })

    const existing = checkResponse.data?.data || []

    // If creating → block if any match exists
    if (!isEditMode && existing.length > 0) {
      showError(
        'Duplicate Entry, This Shipping Line already has this combination.'
      )
      setIsSubmitting(false)
      return
    }

    // If editing → exclude current record
    if (isEditMode) {
      const duplicate = existing.find(
        (item: any) => item.id !== fixedExpensesId
      )
      if (duplicate) {
        showError(
          'Duplicate Entry, This Shipping Line already has this Pickup and Delivery combination.'
        )
        setIsSubmitting(false)
        return
      }
    }

    if (isEditMode) {
      await apiClient.put(`/fixed-expenses/${fixedExpensesId}`, payload)
      showSuccess('Success', 'Fixed Expense updated successfully')
    } else {
      await apiClient.post('/fixed-expenses', payload)
      showSuccess('Success', 'Fixed Expense created successfully')
    }

      onSuccess?.()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }
  

  const selectedFromCypa = useWatch({
    control,
    name: 'cypa_id_from',
  })
  const filteredToCypaOptions = cypaOptions.filter(
    (cypa) => cypa.id !== selectedFromCypa
  )

  const selectedToCypa = useWatch({
    control,
    name: 'cypa_id_to',
  })
  const filteredFromCypaOptions = cypaOptions.filter(
    (cypa) => cypa.id !== selectedToCypa
  )


  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        {/* Name Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Shipping Line Name <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.shipping_line_id}>
            <Select placeholder="Select shipping line"
                {...register('shipping_line_id', { valueAsNumber: true })}
            >
                {shippingLineOptions.map((line) => ( <option key={line.id} value={line.id}> {line.name} </option> ))}
            </Select>

            <FormErrorMessage>
                {errors.shipping_line_id?.message}
            </FormErrorMessage>
          </FormControl>
        </Grid>


        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Pickup Point <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.cypa_id_from}>
            <Select placeholder="Select the Pickup Point"
                {...register('cypa_id_from', { valueAsNumber: true })}
            >
                {filteredFromCypaOptions.map((cypa) => (
                  <option key={cypa.id} value={cypa.id}>{cypa.name}</option>
                ))}
            </Select>

            <FormErrorMessage>
                {errors.cypa_id_from?.message}
            </FormErrorMessage>
          </FormControl>
        </Grid>


        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Deliver To<Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.cypa_id_to}>
            <Select placeholder="Select the Delivery Point"
                {...register('cypa_id_to', { valueAsNumber: true })}
            >
                {filteredToCypaOptions.map((cypa) => (
                  <option key={cypa.id} value={cypa.id}>{cypa.name}</option>
                ))}
            </Select>

            <FormErrorMessage>
                {errors.cypa_id_to?.message}
            </FormErrorMessage>
          </FormControl>
        </Grid>


        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Container Size<Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.container_size}>
            <Select placeholder="Select the Container Size"
                {...register('container_size')}
            >
                {containerSizeOptions.map((size) => (<option key={size} value={size}> {size} </option> ))}
            </Select>

            <FormErrorMessage>
                {errors.container_size?.message}
            </FormErrorMessage>
          </FormControl>
        </Grid>

        
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Docs Fee <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.docs_fee}>
            <Input type="number" {...register('docs_fee', { valueAsNumber: true })} placeholder="Input the Docs Fee price"/>
            <FormErrorMessage>{errors.docs_fee?.message}</FormErrorMessage>
          </FormControl>
        </Grid>


        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Stack Run <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.stack_run}>
            <Input type="number" {...register('stack_run', { valueAsNumber: true })} placeholder="Input the Stack run price"/>
            <FormErrorMessage>{errors.stack_run?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Expenses <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.expenses}>
           <Input type="number" {...register('expenses', { valueAsNumber: true })} placeholder="Input the Expense price"/>
            <FormErrorMessage>{errors.expenses?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Online Booking Fee
          </FormLabel>
          <FormControl isInvalid={!!errors.online_booking_fee}>
            <Input
              type="number"
              {...register('online_booking_fee', { valueAsNumber: true })}
              placeholder="0.00"
              step="0.01"
              min="0"
            />
            <FormErrorMessage>{errors.online_booking_fee?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        {/* Action Buttons */}
        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
          >
            {isEditMode ? 'Save' : 'Create Fixed Expense'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}